import type { CSSProperties } from 'react';

import type { CareersJobApi, LocationType } from '@/modules/careers/types';

// Mirrors the API's handle extraction (LinkedInUtilities.TryExtractLinkedProfileId in finlay-api), which stays the
// final check.
const linkedInProfilePattern = /linkedin\.com\/in\/[^/?#\s]+/i;

export const isLinkedInProfileUrl = (value: string): boolean =>
  linkedInProfilePattern.test(value.trim());

/** The Careers Page, as routed in App.tsx. */
export const careersPath = (companySlug: string) => `/${companySlug}/careers`;

/** One job's page on the Careers Page, as routed in App.tsx. */
export const careersJobPath = (companySlug: string, jobId: string) =>
  `${careersPath(companySlug)}/${jobId}`;

export const locationTypeLabels: Record<LocationType, string> = {
  Onsite: 'On-site',
  Hybrid: 'Hybrid',
  Remote: 'Remote',
};

const contractTypeLabels: Record<
  CareersJobApi['contractTypes'][number],
  string
> = {
  EmploymentContract: 'Employment contract',
  B2B: 'B2B',
};

export const formatContractTypes = ({
  contractTypes,
}: Pick<CareersJobApi, 'contractTypes'>) =>
  contractTypes.map((type) => contractTypeLabels[type]).join(' · ');

const locationTypeOrder: LocationType[] = ['Remote', 'Hybrid', 'Onsite'];

/**
 * The work arrangements an applicant can filter the Careers Page by, with how
 * many jobs offer each. Empty when filtering would not narrow anything: fewer
 * than two arrangements across all jobs.
 */
export const locationTypeFilters = (
  jobs: Pick<CareersJobApi, 'locationTypes'>[],
) => {
  const filters = locationTypeOrder
    .map((type) => ({
      type,
      count: jobs.filter((job) => job.locationTypes.includes(type)).length,
    }))
    .filter(({ count }) => count > 0);

  return filters.length > 1 ? filters : [];
};

/** Places an element in the Careers Page's rise-in stagger (`[data-enter-step]` in index.css). */
export const enterStep = (step: number) => ({
  'data-enter-step': '',
  style: { '--enter-step': step } as CSSProperties,
});
